import { useMemo } from 'react'
import {
  aggregateCrossWorkspaceConnectionHealth,
  type RawConnectionRecord,
} from '../../../services/integrationsAuditAggregator'
import { Card } from '../../Common'
import './IntegrationsAuditPanel.css'

export interface IntegrationsAuditPanelProps {
  actorUid?: string
  initialConnections?: RawConnectionRecord[]
}

export const IntegrationsAuditPanel = ({
  initialConnections,
}: IntegrationsAuditPanelProps) => {
  const audit = useMemo(
    () => aggregateCrossWorkspaceConnectionHealth(initialConnections ?? []),
    [initialConnections],
  )

  return (
    <section className="ownerPanel integrationsAuditPanel" aria-label="Integrations audit">
      <div className="ownerPanelHeader">
        <div>
          <span className="ownerEyebrow">Link Hub Audit</span>
          <h2>Integrations Audit</h2>
          <p>Cross-workspace connection health, sync diagnostics, and stream telemetry.</p>
        </div>
      </div>

      <div className="integrationsAuditKpiGrid">
        <div className="integrationsAuditKpiCard">
          <span>Total Connections</span>
          <strong>{audit.totalConnections}</strong>
        </div>
        <div className="integrationsAuditKpiCard is-good">
          <span>Active & Healthy</span>
          <strong>{audit.activeCount}</strong>
        </div>
        <div className="integrationsAuditKpiCard is-warn">
          <span>Degraded</span>
          <strong>{audit.degradedCount}</strong>
        </div>
        <div className="integrationsAuditKpiCard is-danger">
          <span>Attention Needed</span>
          <strong>{audit.failedCount}</strong>
        </div>
      </div>

      <Card
        variant="outline"
        accent="primary"
        title="Shipped provider health"
        subtitle="Live connection status across workspaces."
      >
        <div className="integrationsAuditProviderGrid">
          {Object.values(audit.byProvider).map((provider) => (
            <div
              key={provider.providerId}
              className={`integrationsAuditProviderCard tone-${provider.healthTone}`}
              data-provider-id={provider.providerId}
            >
              <div className="integrationsAuditProviderHeader">
                <strong className="integrationsAuditProviderName">{provider.name}</strong>
                <span className={`integrationsAuditBadge is-${provider.healthTone}`}>
                  {provider.healthLabel}
                </span>
              </div>
              <div className="integrationsAuditProviderCounts">
                <span>Active: {provider.activeCount}</span>
                <span>Degraded: {provider.degradedCount}</span>
                <span>Failed: {provider.failedCount}</span>
              </div>
              <div className="integrationsAuditProviderMeta">
                <small>
                  {provider.lastSyncAt
                    ? `Last sync: ${new Date(provider.lastSyncAt).toLocaleString()}`
                    : 'No sync recorded'}
                </small>
              </div>
            </div>
          ))}
        </div>
      </Card>

      <Card
        variant="outline"
        accent="none"
        title="Inbound events stream"
        subtitle="Cross-workspace event ingestion and delivery replay."
      >
        <div className="integrationsAuditComingSoonBox">
          <div className="integrationsAuditComingSoonBadge">Coming soon</div>
          <p className="integrationsAuditComingSoonText">{audit.inboundStreamMessage}</p>
          <small className="integrationsAuditComingSoonNote">
            Real-time cross-workspace webhook ingestion, envelope v1 decoding, and delivery replay
            will land in an upcoming release.
          </small>
        </div>
      </Card>
    </section>
  )
}
